import React, { useState, useEffect } from 'react';
import logger from "../utils/logger";

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Form, Button } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useTheme } from './themeControl/ThemeProvider';
import { useAlert } from '../providers/AlertProvider';
import { useAuthProvider } from '../providers/AuthProvider';

import { validSession } from '../components/lib/validSession';
import VehicleEditModal from '../components/custom/VehicleEditModal';
import { ActivityIndicator } from '../components/custom/ActivityIndicator';
import { sortList, formatDate, downloadCSV } from './lib/functions';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faAnglesLeft, faAnglesRight, faTruck, faTruckFast, faCubes, faGears, faBan, faPlus, faFileArrowUp, faFileAlt, faTrash, faClipboardList, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';


// Api
import { fetch_all_vehicles, suspend_vehicle, update_vehicle, add_vehicle, fetch_vehicle_by_reg, fetch_vehicle_files, upload_vtg7, delete_vehicle_file, get_vtg7_signed_url, download_vehicle_audit, download_vehicle_files_zip, generate_vehicle_qr } from '../api/vehicles';
import { fetch_all_profiles } from '../api/profiles';

// import css
import './css/GlobalStyles.css';
import './css/DriversStyles.css';
import './css/VehicleStyles.css';
import './css/LeftNavigation.css';
import './css/Styles.css';

// import images
import './img/light-driver.png';
import './img/dark-driver.png';
import './img/light-admin.png';
import './img/dark-admin.png';
import './img/light-service.png';
import './img/dark-service.png';
import './img/edit.png';
import './img/padlock.png';
import './img/unlock.png';
import './img/unit.png';
import './img/rigid.png';
import './img/trailer.png';
import './img/mhe.png';

const VehiclesPage = () => {
    const { theme } = useTheme();
    const { showAlert } = useAlert();
    const { logout, isAdmin, isManager, isDev, permissions } = useAuthProvider();

    const canEdit = isDev || permissions.perm_vehicles_edit;

    const navigate  = useNavigate();

    // loading state
    const [isLoading, setIsLoading] = useState(false);

    // Images
    const driver = require(`./img/${theme}-driver.png`);
    const admin = require(`./img/${theme}-admin.png`);
    const service = require(`./img/${theme}-service.png`);
    const unit = require(`./img/unit.png`);
    const rigid = require(`./img/rigid.png`);
    const trailer = require(`./img/trailer.png`);
    const edit = require(`./img/edit.png`);
    const padlock = require(`./img/padlock.png`);
    const unlock = require(`./img/unlock.png`);
    const mheImage = require(`./img/mhe.png`);

    // State to manage active tab
    const [activeTab, setActiveTab] = useState('units');
    const [showLeftNav, setShowLeftNav] = useState(false);

    // States to manage selected profile
    const [selectedVehicle, setSelectedVehicle] = useState(null);

    // State for account lists
    const [unitList, setUnits] = useState([]);
    const [rigidList, setRigids] = useState([]);
    const [trailerList, setTrailers] = useState([]);
    const [mheList, setMheList] = useState([]);
    const [inactiveList, setInactive] = useState([]);
    const [serviceCompanies, setServiceCompanies] = useState([]);

    const [type, setType] = useState('UNIT');
    const [serviceCompanyFilter, setServiceCompanyFilter] = useState('all');
    const selectedCompanyId = serviceCompanyFilter === 'all' ? null : serviceCompanies.find(c => String(c.id) === serviceCompanyFilter)?.id ?? null;

    // User edit modal-------------------------------------------------------------------------------------------------------------------
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleOpenModal = () => {
        logger.log('Opening Modal');
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    // VTG7 / vehicle files -------------------------------------------------------------------------------------------------------------------
    const [vehicleFiles, setVehicleFiles] = useState([]);
    const [vtg7Uploading, setVtg7Uploading] = useState(false);
    const [auditDownloading, setAuditDownloading] = useState(false);
    const [filesZipDownloading, setFilesZipDownloading] = useState(false);
    const [qrDownloading, setQrDownloading] = useState(false);

    const loadVehicleFiles = async (vehicleId) => {
        const result = await fetch_vehicle_files(vehicleId);
        if (result.status === 200) {
            setVehicleFiles(Array.isArray(result.data) ? result.data : []);
        } else {
            setVehicleFiles([]);
        }
    };

    // Reload files whenever the selected vehicle changes
    const handleSelectVehicle = (vehicle) => {
        setSelectedVehicle(vehicle);
        setVehicleFiles([]);
        if (vehicle) loadVehicleFiles(vehicle.id);
    };

    const handleVtg7Upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !selectedVehicle) return;
        setVtg7Uploading(true);
        const result = await upload_vtg7(selectedVehicle.id, file, file.name);
        setVtg7Uploading(false);
        if (result.status === 201) {
            showAlert({ message: 'VTG7 uploaded successfully', type: 'success', buttons: [{ label: 'Close' }] });
            loadVehicleFiles(selectedVehicle.id);
        } else {
            showAlert({ message: result.data?.error || 'Failed to upload VTG7', type: 'danger', buttons: [{ label: 'Close' }] });
        }
        e.target.value = '';
    };

    const handleVtg7View = async (filePath) => {
        const result = await get_vtg7_signed_url(filePath);
        if (result.status === 200 && result.data?.signed_url) {
            window.open(result.data.signed_url, '_blank', 'noopener,noreferrer');
        } else {
            showAlert({ message: 'Could not open file', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    const handleVtg7Delete = async (fileId) => {
        if (!window.confirm('Are you sure you want to delete this VTG7?')) return;
        const result = await delete_vehicle_file(fileId);
        if (result.status === 200) {
            setVehicleFiles(prev => prev.filter(f => f.id !== fileId));
        } else {
            showAlert({ message: result.data?.error || 'Failed to delete file', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    const handleDownloadAudit = async () => {
        if (!selectedVehicle) return;
        setAuditDownloading(true);
        const result = await download_vehicle_audit(selectedVehicle.id, selectedVehicle.reg);
        setAuditDownloading(false);
        if (result.status !== 200) {
            showAlert({ message: result.message || 'Failed to download audit', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    const handleDownloadFilesZip = async () => {
        if (!selectedVehicle) return;
        setFilesZipDownloading(true);
        const result = await download_vehicle_files_zip(selectedVehicle.id, selectedVehicle.reg);
        setFilesZipDownloading(false);
        if (result.status !== 200) {
            showAlert({ message: result.message || 'Failed to download files', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    const handleGenerateQr = async () => {
        if (!selectedVehicle) return;
        setQrDownloading(true);
        const result = await generate_vehicle_qr(selectedVehicle.id, selectedVehicle.reg);
        setQrDownloading(false);
        if (result.status !== 200) {
            showAlert({ message: result.message || 'Failed to generate QR code', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    const handleUpdate = async (updatedData) => {
        setIsLoading(true);
        // Handle the updated data, e.g., send it to the backend
        // suspend account by the id
        const result = await update_vehicle(updatedData);

        if (result.status === 200) {
            setIsLoading(false);
            showAlert({
                message: `${result.data.reg} has been updated`,
                type: 'success',
                buttons: [{
                    label: 'Close',
                    onClick: () => {
                        setSelectedVehicle(null);
                        setActiveTab('units');
                        fetchVehicles(); // refresh the list
                    }
                }],
            });

        } else {
            setIsLoading(false);
            showAlert({
                message: 'Failed to update vehicle',
                type: 'danger',
                buttons: [{ label: 'Retry', type: 'danger' }],
            });
        }
    };


    // function to fetch profiles from backend----------------------------------------------------------------------------------------------
    const fetchVehicles = async () => {
        setIsLoading(true);
        // check for valid session to stop a loop on component mount
        if (!validSession()) {
            setIsLoading(false);
            showAlert({
                message: 'Session expired, please login again',
                type: 'danger',
                buttons: [{
                    label: 'Login',
                    type: 'danger',
                    onClick: () => {
                        logout();
                        navigate('/')
                        logger.log("Session expired, failed to fetch vehicles");
                    }
                }],
            });
            return;
        };

        try {
            // Fetch both vehicles and service companies
            const [vehiclesResult, profilesResult] = await Promise.all([
                fetch_all_vehicles(),
                fetch_all_profiles()
            ]);

            if (vehiclesResult.status === 200) {
                const vehicles = vehiclesResult.data || [];

                // sort vehicles by registration
                const sortedVehicles = sortList(vehicles, 'reg');

                // filter active profiles
                const activeVehicles = sortedVehicles.filter(vehicle => vehicle.status === 'active');

                // set account lists
                setUnits(activeVehicles.filter(vehicle => vehicle.type === 'UNIT'));
                setRigids(activeVehicles.filter(vehicle => vehicle.type === 'RIGID'));
                setMheList(activeVehicles.filter(vehicle => vehicle.type === 'MHE'));

                // sort trailers: regular first, then moffett (M suffix), then double-deck (D suffix)
                const sortNaturally = (arr) => arr.slice().sort((a, b) =>
                    a.reg.localeCompare(b.reg, undefined, { numeric: true, sensitivity: 'base' })
                );
                const allTrailers = activeVehicles.filter(vehicle => vehicle.type === 'TRAILER');
                const moffettTrailers = allTrailers.filter(v => v.reg.slice(-1) === 'M');
                const doubleDeckTrailers = allTrailers.filter(v => v.reg.slice(-1) === 'D');
                const otherTrailers = allTrailers.filter(v => !moffettTrailers.includes(v) && !doubleDeckTrailers.includes(v));
                setTrailers([...sortNaturally(otherTrailers), ...sortNaturally(moffettTrailers), ...sortNaturally(doubleDeckTrailers)]);

                // filter inactive vehicles (remove the first vehicle from the list, this is reserved for external trailers)
                const inactiveVehicles = sortedVehicles.filter(vehicle => vehicle.status === 'inactive' && vehicle.id !== 1);
                setInactive(inactiveVehicles);
            }

            // Fetch service companies for hire information
            if (profilesResult.status === 200) {
                const serviceProfiles = profilesResult.data.filter(profile => 
                    profile.group === 'SERVICE' && profile.status === 'active'
                );
                setServiceCompanies(serviceProfiles);
            }

            setIsLoading(false);

        } catch (error) {
            setIsLoading(false);
            showAlert({
                message: 'Something went wrong, please try again',
                type: 'danger',
                buttons: [{
                    label: 'Login',
                    type: 'danger',
                    onClick: () => {
                        logout();
                        navigate('/')
                        logger.log("Session expired, please login again");
                    }
                }],
            });
        }
    };

    // fetch data from backend on component mount
    useEffect(() => {
        fetchVehicles();
    }, []);

    // Helper function to get service company name by ID
    const getServiceCompanyName = (hirerId) => {
        const company = serviceCompanies.find(service => service.id === hirerId);
        return company ? company.name : 'Unknown Company';
    };


    // Functions to create account ----------------------------------------------------------------------------------------------
    const {register, watch, handleSubmit, reset, formState:{errors}} = useForm();

    const submitForm = async (data) => {
        setIsLoading(true);

        // check vehicle not already in the system
        const { status: checkVehicleStatus } = await fetch_vehicle_by_reg(data.reg);
        
        if (checkVehicleStatus === 200 || checkVehicleStatus === 201) {
            setIsLoading(false);
            showAlert({
                message: 'Vehicle already in the system',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }],
            });
            return;
        }

        // add vehicle to the system
        const { status } = await add_vehicle(data.reg, type);
        logger.log("Add vehicle response: ", status);
        // Show success message with server response
        setIsLoading(false);
        showAlert({
            message: status === 200 || status === 201 ? 'Vehicle Added Successfully' : 'Failed to create vehicle',
            type: status === 200 || status === 201 ? 'success' : 'danger',
            buttons: [
                {
                    label: status === 200 || status === 201 ? 'Close' : 'Retry',
                    onClick: () => {
                        setSelectedVehicle(null);
                        setActiveTab('units');
                        fetchVehicles(); // Refresh the list
                    }
                }
            ],
        });

        // Reset the form
        reset();
    }

    // Function to suspend account ----------------------------------------------------------------------------------------------
    const handleSuspend = async (vehicle) => {
        setIsLoading(true);

        // suspend account by the id
        const result = await suspend_vehicle(vehicle.id, vehicle.status === 'active' ? 'inactive' : 'active');

        if (result.status === 200) {
            setIsLoading(false);
            showAlert({
                message: `Vehicle now ${result.data.status}`,
                type: 'success',
                buttons: [{
                    label: 'Close',
                    onClick: () => {
                        setSelectedVehicle(null);
                        setActiveTab('units');
                        fetchVehicles(); // refresh the list
                    }
                }],
            });

        } else {
            setIsLoading(false);
            showAlert({
                message: 'Failed to change vehicle status',
                type: 'danger',
                buttons: [{ label: 'Retry', type: 'danger' }],
            });
        }
    }

    if (!isAdmin && !isDev && !permissions.perm_vehicles_view) {
        return (
            <div style={{ textAlign: 'center', marginTop: '30vh', height: '100vh', color: 'red' }}>
                <h3>Access Denied</h3>
                <p>You do not have permission to view this page</p>
            </div>
        )
    }

    return (
        <div id={theme} className="container-fluid closed-drivers-container">

            <Row>
                {/* Left Navigation Panel */}
                <Col className={showLeftNav ? "left-nav" : "closed-nav"} style={{ height: '100vh', paddingBottom: '150px' }} onMouseEnter={() => setShowLeftNav(true)} onMouseLeave={() => setShowLeftNav(false)}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%' }}>
                        <li title="Units" className={`nav-item ${activeTab === 'units' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'units' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => {setActiveTab('units'); setSelectedVehicle(null);} : null}><FontAwesomeIcon icon={faTruck} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Units</span></li>
                        <li title="Rigids" className={`nav-item ${activeTab === 'rigids' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'rigids' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => {setActiveTab('rigids'); setSelectedVehicle(null);} : null}><FontAwesomeIcon icon={faTruckFast} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Rigids</span></li>
                        <li title="Trailers" className={`nav-item ${activeTab === 'trailers' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'trailers' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => {setActiveTab('trailers'); setSelectedVehicle(null);} : null}><FontAwesomeIcon icon={faCubes} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Trailers</span></li>
                        <li title="MHE" className={`nav-item ${activeTab === 'forklifts' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'forklifts' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => {setActiveTab('forklifts'); setSelectedVehicle(null);} : null}><FontAwesomeIcon icon={faGears} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">MHE</span></li>
                        <li title="Inactive" className={`nav-item ${activeTab === 'inactive' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'inactive' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => {setActiveTab('inactive'); setSelectedVehicle(null);} : null}><FontAwesomeIcon icon={faBan} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Inactive</span></li>
                        <li title="Add New Vehicle" className={`nav-item ${activeTab === 'addVehicle' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : canEdit ? 'pointer' : 'default', background: activeTab === 'addVehicle' ? 'rgba(25, 151, 235, 0.8)' : 'transparent', opacity: canEdit ? 1 : 0.4 }} onClick={!isLoading && canEdit ? () => {setActiveTab('addVehicle'); setSelectedVehicle(null);} : null}><FontAwesomeIcon icon={faPlus} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Add New Vehicle</span></li>
                    </ul>

                    {showLeftNav && (<>
                    {/* Service Company Filter */}
                    {activeTab !== 'addVehicle' && (
                        <div style={{ marginTop: '16px' }}>
                            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Filter by Service Company</label>
                            <select
                                value={serviceCompanyFilter}
                                onChange={e => { setServiceCompanyFilter(e.target.value); setSelectedVehicle(null); }}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13 }}
                            >
                                <option value="all">All Companies</option>
                                {serviceCompanies.map(company => (
                                    <option key={company.id} value={company.id}>{company.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Drivers List */}
                    {activeTab === 'units' && (
                        <div className="drivers-list" style={{ marginTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                                <h5><strong>Active Units</strong></h5>
                                <FontAwesomeIcon icon={faDownload} style={{ cursor: 'pointer', width: 20 }} onClick={() => {downloadCSV(unitList)}} />
                            </div>

                            <ul style={{ listStyle: 'none', padding: 0 }}>
                                {unitList.filter(v => selectedCompanyId === null || v.hirer_id === selectedCompanyId).map((unit) => (
                                    <li
                                        key={unit.id}
                                        style={{ cursor: isLoading ? 'not-allowed' : 'pointer', width: '100%', padding: '10px', borderRadius: 5, border: selectedVehicle?.id === unit.id ? '1px solid #0588f3' : unit.vor ? '1px solid red' : 'transparent' }}
                                        onClick={!isLoading ? () => handleSelectVehicle(unit) : null}
                                    >
                                        {unit.reg}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                        
                    {/* Admin List */}
                    {activeTab === 'rigids' && (
                        <div className="drivers-list" style={{ marginTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                                <h5><strong>Active Rigids</strong></h5>
                                <FontAwesomeIcon icon={faDownload} style={{ cursor: 'pointer', width: 20 }} onClick={() => {downloadCSV(rigidList)}} />
                            </div>

                            <ul style={{ listStyle: 'none', padding: 0 }}>
                                {rigidList.filter(v => selectedCompanyId === null || v.hirer_id === selectedCompanyId).map((rigid) => (
                                    <li
                                        key={rigid.id}
                                        style={{ cursor: isLoading ? 'not-allowed' : 'pointer', width: '100%', padding: '10px', borderRadius: 5, border: selectedVehicle?.id === rigid.id ? '1px solid #0588f3' : rigid.vor ? '1px solid red' : 'transparent' }}
                                        onClick={!isLoading ? () => handleSelectVehicle(rigid) : null}
                                    >
                                        {rigid.reg}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Service List */}
                    {activeTab === 'trailers' && (
                        <div className="drivers-list" style={{ marginTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                                <h5><strong>Active Trailers</strong></h5>
                                <FontAwesomeIcon icon={faDownload} style={{ cursor: 'pointer', width: 20 }} onClick={() => {downloadCSV(trailerList)}} />
                            </div>

                            <ul style={{ listStyle: 'none', padding: 0 }}>
                                {trailerList.filter(v => selectedCompanyId === null || v.hirer_id === selectedCompanyId).map((trailer) => (
                                    <li
                                        key={trailer.id}
                                        style={{ cursor: isLoading ? 'not-allowed' : 'pointer', width: '100%', padding: '10px', borderRadius: 5, border: selectedVehicle?.id === trailer.id ? '1px solid #0588f3' : trailer.vor ? '1px solid red' : 'transparent' }}
                                        onClick={!isLoading ? () => handleSelectVehicle(trailer) : null}
                                    >
                                        {trailer.reg}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* MHE List */}
                    {activeTab === 'forklifts' && (
                        <div className="drivers-list" style={{ marginTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                                <h5><strong>Active MHE</strong></h5>
                                <FontAwesomeIcon icon={faDownload} style={{ cursor: 'pointer', width: 20 }} onClick={() => {downloadCSV(mheList)}} />
                            </div>

                            <ul style={{ listStyle: 'none', padding: 0 }}>
                                {mheList.filter(v => selectedCompanyId === null || v.hirer_id === selectedCompanyId).map((mhe) => (
                                    <li
                                        key={mhe.id}
                                        style={{ cursor: isLoading ? 'not-allowed' : 'pointer', width: '100%', padding: '10px', borderRadius: 5, border: selectedVehicle?.id === mhe.id ? '1px solid #0588f3' : mhe.vor ? '1px solid red' : 'transparent' }}
                                        onClick={!isLoading ? () => handleSelectVehicle(mhe) : null}
                                    >
                                        {mhe.reg}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Inactive List */}
                    {activeTab === 'inactive' && (
                        <div className="drivers-list" style={{ marginTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                                <h5><strong>Inactive Vehicles</strong></h5>
                                <FontAwesomeIcon icon={faDownload} style={{ cursor: 'pointer', width: 20 }} onClick={() => {downloadCSV(inactiveList)}} />
                            </div>

                            <ul style={{ listStyle: 'none', padding: 0 }}>
                                {inactiveList.filter(v => selectedCompanyId === null || v.hirer_id === selectedCompanyId).map((inactive) => (
                                    <li
                                        key={inactive.id}
                                        style={{ cursor: isLoading ? 'not-allowed' : 'pointer', width: '100%', padding: '10px', borderRadius: 5, border: selectedVehicle?.id === inactive.id ? '1px solid #0588f3' : inactive.vor ? '1px solid red' : 'transparent' }}
                                        onClick={!isLoading ? () => handleSelectVehicle(inactive) : null}
                                    >
                                        {inactive.reg}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    </>)}
                </Col>

                {/* Right Content Panel */}
                <Col className="closed-nav-content" style={{ padding: '20px' }}>
                    {/* Loading Animation */}
                    {isLoading ? (
                            <div style={{ alignSelf: 'center', justifyContent: 'center', marginTop: '30vh' }}>
                                <ActivityIndicator />
                            </div>
                    ) : (
                    <>

                    {/* Edit User Data Modal */}
                    {isModalOpen && (
                        <VehicleEditModal
                            theme={theme}
                            vehicle={selectedVehicle}
                            isOpen={isModalOpen}
                            onClose={handleCloseModal}
                            onSubmit={handleUpdate}
                        />                        
                    )}


                    <div className="accounts-conatiner" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {/* Right Content Drivers */}
                        {activeTab !== 'addVehicle' && (
                            <>

                            {/* Header */}
                            <span style={{ textAlign: 'center'}}>
                            {(() => {
                                switch (activeTab) {
                                    case 'units':
                                        return <h3>Units Information</h3>;
                                    case 'rigids':
                                        return <h3>Rigids Information</h3>;
                                    case 'trailers':
                                        return <h3>Trailers Information</h3>;
                                    case 'forklifts':
                                        return <h3>MHE Information</h3>;
                                    case 'inactive':
                                        return <h3>Inactive Information</h3>;
                                    default:
                                        return null; // Handle unexpected cases
                                }
                            })()}

                            {/* edit account buttons */}
                            {selectedVehicle && canEdit && (
                                <div className="accounts-buttons">
                                    <span className="type-button" onClick={() => handleOpenModal()}><img src={edit} width={110} height={110} alt="edit-img" same-site="true"></img>Edit</span>
                                    {activeTab !== 'inactive' ? (
                                        <span className="type-button" onClick={() => handleSuspend(selectedVehicle)}><img src={padlock} width={110} height={110} alt="suspend-img" same-site="true"></img>Suspend</span>
                                    ) : (
                                        <span className="type-button" onClick={() => handleSuspend(selectedVehicle)}><img src={unlock} width={110} height={110} alt="activate-img" same-site="true"></img>Activate</span>
                                    )}
                                </div>                            
                            )}

                            {/* Account Details */}
                            </span>
                                {selectedVehicle ? (
                                    <div style={{ maxWidth: 600, margin: '20px auto', padding: '0 20px', textAlign: 'center' }}>
                                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                                            {selectedVehicle.vor && (
                                                <span style={{ color: 'red', fontSize: 20, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 5 }}>
                                                    <img src={padlock} width={30} height={30} alt="vor-img" />
                                                    VOR
                                                </span>
                                            )}
                                        </div>

                                        <p><strong>Reg:</strong> {selectedVehicle.reg}</p>

                                        <p><strong>Year:</strong> {selectedVehicle.year_manufactured}</p>

                                        {(selectedVehicle.type === 'TRAILER' || selectedVehicle.type === "MHE") ? (
                                            <p><strong>C Number:</strong> {selectedVehicle.chassis}</p>
                                        ) : null}

                                        {(selectedVehicle.type === 'RIGID' || selectedVehicle.type === 'UNIT') ? (
                                            <p><strong>Cab Phone:</strong> {selectedVehicle.cab_phone?? ''}</p>
                                        ) : null}

                                        {(() => {
                                            const expiryFields = [
                                                { label: 'Inspection Exp', key: 'inspection_expiry' },
                                                { label: 'MOT Exp', key: 'mot_expiry' },
                                                { label: 'Tax Exp', key: 'tax_expiry' },
                                                { label: 'Loler Exp', key: 'loler_expiry' },
                                            ];

                                            return expiryFields.map(({ label, key }) => {
                                                const date = selectedVehicle?.[key];
                                                if (!date) return null;

                                                return (
                                                    <p key={key}>
                                                        <strong>{label}:</strong> {formatDate(date)}
                                                    </p>
                                                );
                                            });
                                        })()}

                                        {selectedVehicle.inspection_interval != null && (
                                            <p><strong>Inspection Interval:</strong> {selectedVehicle.inspection_interval} weeks</p>
                                        )}

                                        {selectedVehicle.on_hire && (
                                            <>
                                            <p><strong>Hire Company:</strong> {
                                                selectedVehicle.hirer_id 
                                                    ? getServiceCompanyName(selectedVehicle.hirer_id)
                                                    : 'Not specified'
                                            }</p>
                                            <p><strong>Hire From:</strong> {selectedVehicle.hire_from ? formatDate(selectedVehicle.hire_from) : 'Not specified'}</p>
                                            <p><strong>Hire To:</strong> {selectedVehicle.hire_to ? formatDate(selectedVehicle.hire_to) : 'Not specified'}</p>
                                            </>
                                        )}

                                        {(selectedVehicle.type === 'UNIT' || selectedVehicle.type === 'RIGID') && (
                                            <p><strong>London Registered:</strong> {selectedVehicle.london_registered ? 'Yes' : 'No' }</p>
                                        )}

                                        {selectedVehicle.type !== 'MHE' && selectedVehicle.height && (
                                            <p><strong>Height:</strong> {(() => {
                                                const m = parseFloat(selectedVehicle.height);
                                                if (isNaN(m)) return selectedVehicle.height;
                                                const totalFeet = m * 3.28084;
                                                const feet = Math.floor(totalFeet);
                                                const inches = Math.round((totalFeet - feet) * 12);
                                                return `${m}m (${feet}' ${inches}")`;
                                            })()}</p>
                                        )}

                                        {selectedVehicle.supplier_ref && (
                                            <p><strong>Supplier Ref:</strong> {selectedVehicle.supplier_ref}</p>
                                        )}

                                        <span style={{color: selectedVehicle.status === "active" ? 'green' : 'red'}}>
                                            <p><strong>Status:</strong> {selectedVehicle.status}</p>
                                        </span>

                                        {/* ── VTG7 Documents ─────────────────────────── */}
                                        <hr style={{ margin: '16px 0' }} />
                                        <div style={{ textAlign: 'left' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                                <strong><FontAwesomeIcon icon={faFileAlt} style={{ marginRight: 6, width: 14 }} />VTG7 Documents</strong>
                                                {canEdit && (
                                                    <label style={{ cursor: vtg7Uploading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#0588f3' }}>
                                                        <FontAwesomeIcon icon={faFileArrowUp} style={{ width: 14 }} />
                                                        {vtg7Uploading ? ' Uploading…' : ' Upload VTG7'}
                                                        <input
                                                            type="file"
                                                            accept=".pdf,application/pdf"
                                                            style={{ display: 'none' }}
                                                            disabled={vtg7Uploading}
                                                            onChange={handleVtg7Upload}
                                                        />
                                                    </label>
                                                )}
                                            </div>
                                            {vehicleFiles.filter(f => f.file_type === 'vtg7').length === 0 ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, marginTop: 6 }}>
                                                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#e65100', width: 16, flexShrink: 0 }} />
                                                    <span style={{ fontSize: 13, color: '#7d4e00', fontWeight: 600 }}>No VTG7 document uploaded for this vehicle.</span>
                                                </div>
                                            ) : (
                                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                                    {vehicleFiles.filter(f => f.file_type === 'vtg7').map(f => (
                                                        <li key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #eee', fontSize: 13 }}>
                                                            <span
                                                                style={{ flex: 1, cursor: 'pointer', color: '#0588f3', textDecoration: 'underline' }}
                                                                onClick={() => handleVtg7View(f.file_path)}
                                                                title="Click to view"
                                                            >
                                                                {f.display_name || f.file_path}
                                                            </span>
                                                            <span style={{ color: '#999', fontSize: 11 }}>
                                                                {f.created_at ? new Date(f.created_at).toLocaleDateString('en-GB') : ''}
                                                            </span>
                                                            {canEdit && (
                                                                <FontAwesomeIcon
                                                                    icon={faTrash}
                                                                    style={{ cursor: 'pointer', color: '#dc3545', width: 14 }}
                                                                    onClick={() => handleVtg7Delete(f.id)}
                                                                    title="Delete VTG7"
                                                                />
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>

                                        {/* ── Download Audit + Files ZIP ──────────────── */}
                                        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <button
                                                onClick={handleDownloadAudit}
                                                disabled={auditDownloading}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    padding: '8px 14px',
                                                    borderRadius: 5,
                                                    border: 'none',
                                                    backgroundColor: '#1a73e8',
                                                    color: '#fff',
                                                    cursor: auditDownloading ? 'not-allowed' : 'pointer',
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    opacity: auditDownloading ? 0.7 : 1,
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faClipboardList} style={{ width: 16 }} />
                                                {auditDownloading ? 'Generating…' : 'Download Audit'}
                                            </button>
                                            <button
                                                onClick={handleDownloadFilesZip}
                                                disabled={filesZipDownloading}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    padding: '8px 14px',
                                                    borderRadius: 5,
                                                    border: 'none',
                                                    backgroundColor: '#28a745',
                                                    color: '#fff',
                                                    cursor: filesZipDownloading ? 'not-allowed' : 'pointer',
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    opacity: filesZipDownloading ? 0.7 : 1,
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faDownload} style={{ width: 14 }} />
                                                {filesZipDownloading ? 'Zipping…' : 'Download All Files'}
                                            </button>
                                            {selectedVehicle.type === 'MHE' && (
                                                <button
                                                    onClick={handleGenerateQr}
                                                    disabled={qrDownloading}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        padding: '8px 14px',
                                                        borderRadius: 5,
                                                        border: 'none',
                                                        backgroundColor: '#6f42c1',
                                                        color: '#fff',
                                                        cursor: qrDownloading ? 'not-allowed' : 'pointer',
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                        opacity: qrDownloading ? 0.7 : 1,
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faDownload} style={{ width: 14 }} />
                                                    {qrDownloading ? 'Generating…' : 'Download QR Code'}
                                                </button>
                                            )}
                                        </div>

                                    </div>
                                ) : (
                                    <span style={{ textAlign: 'center'}}>
                                        {(() => {
                                            switch (activeTab) {
                                                case 'units':
                                                    return <p>Please select a unit from the list.</p>;
                                                case 'rigids':
                                                    return <p>Please select a rigid from the list.</p>;
                                                case 'trailers':
                                                    return <p>Please select a trailer from the list.</p>;
                                                case 'forklifts':
                                                    return <p>Please select MHE from the list.</p>;
                                                case 'inactive':
                                                    return <p>Please select a vehicle from the list.</p>;
                                                default:
                                                    return null; // Handle unexpected cases
                                            }
                                        })()}
                                    </span>
                                )}
                            </>
                        )}
                    </div>

                    {/* Right Content Add Account */}
                    {activeTab === 'addVehicle' && (
                        <>
                            <span style={{ textAlign: 'center' }}>
                                <h3>Add new vehicle</h3>
                            </span>

                            {/* account type selection */}
                            <div style={{ textAlign: 'center', flexDirection: 'row' }}>
                                <span className="vehicles-button" onClick={() => setType('UNIT')} style={{ 
                                    backgroundColor: type === "UNIT" ? (theme === 'light' ? '#e8f4fd' : '#1a2332') : 'transparent',
                                    border: type === "UNIT" ? `2px solid ${theme === 'light' ? '#007bff' : '#4dabf7'}` : '2px solid transparent',
                                    boxShadow: type === "UNIT" ? `0 0 10px ${theme === 'light' ? 'rgba(0, 123, 255, 0.3)' : 'rgba(77, 171, 247, 0.4)'}` : 'none',
                                    transition: 'all 0.3s ease'
                                }}><img src={unit} width={200} height={110} alt="driver-img" same-site="true"></img>Unit</span>
                                <span className="vehicles-button" onClick={() => setType('RIGID')} style={{ 
                                    backgroundColor: type === "RIGID" ? (theme === 'light' ? '#e8f4fd' : '#1a2332') : 'transparent',
                                    border: type === "RIGID" ? `2px solid ${theme === 'light' ? '#007bff' : '#4dabf7'}` : '2px solid transparent',
                                    boxShadow: type === "RIGID" ? `0 0 10px ${theme === 'light' ? 'rgba(0, 123, 255, 0.3)' : 'rgba(77, 171, 247, 0.4)'}` : 'none',
                                    transition: 'all 0.3s ease'
                                }}><img src={rigid} width={200} height={110} alt="driver-img" same-site="true"></img>Rigid</span>
                                <span className="vehicles-button" onClick={() => setType('TRAILER')} style={{ 
                                    backgroundColor: type === "TRAILER" ? (theme === 'light' ? '#e8f4fd' : '#1a2332') : 'transparent',
                                    border: type === "TRAILER" ? `2px solid ${theme === 'light' ? '#007bff' : '#4dabf7'}` : '2px solid transparent',
                                    boxShadow: type === "TRAILER" ? `0 0 10px ${theme === 'light' ? 'rgba(0, 123, 255, 0.3)' : 'rgba(77, 171, 247, 0.4)'}` : 'none',
                                    transition: 'all 0.3s ease'
                                }}><img src={trailer} width={200} height={110} alt="driver-img" same-site="true"></img>Trailer</span>
                                <span className="vehicles-button" onClick={() => setType('MHE')} style={{ 
                                    backgroundColor: type === "MHE" ? (theme === 'light' ? '#e8f4fd' : '#1a2332') : 'transparent',
                                    border: type === "MHE" ? `2px solid ${theme === 'light' ? '#007bff' : '#4dabf7'}` : '2px solid transparent',
                                    boxShadow: type === "MHE" ? `0 0 10px ${theme === 'light' ? 'rgba(0, 123, 255, 0.3)' : 'rgba(77, 171, 247, 0.4)'}` : 'none',
                                    transition: 'all 0.3s ease'
                                }}><img src={mheImage} width={200} height={110} alt="driver-img" same-site="true"></img>MHE</span>
                            </div>
                            
                            <>
                                <form>

                                {/* account type */}
                                <span style={{ textAlign: 'center' }}>
                                    <h3>{type}</h3>
                                </span>

                                    {/*"""Name OR Company"""*/}
                                    <Form.Group className="signup-form">
                                        <Form.Label>{type === 'TRAILER' ? 'Trailer No.' : type === 'MHE' ? 'ID Number' : 'Registration'}</Form.Label>
                                        <Form.Control type="text"
                                            placeholder={type === 'TRAILER' ? 'No.' : type === 'MHE' ? 'ID' : 'Reg'}
                                            {...register('reg', { required: true, maxLength: 10 })} /*Validates that a name is entered correctly*/ />
                                    </Form.Group>
                                    {/*"""Error handling"""*/}
                                    <div className="signup-error-container">
                                        {errors.reg && <p className="signup-errors"><small>{type === 'TRAILER' ? 'Trailer no. required' : type === 'MHE' ? 'ID Required': 'Vehicle registration required'}</small></p>}
                                        {errors.reg?.type === "maxLength" && <p className="signup-errors"><small>Max length 10 characters</small></p>}
                                    </div>


                                    {/*"""Submit Button"""*/}
                                    <Form.Group>
                                        <div className="signup-button-container">
                                            <Button as="sub" variant="primary" onClick={handleSubmit(submitForm)}>Add To Database</Button>
                                        </div>
                                    </Form.Group>

                                </form>
                            </>
                        </>
                    )}
                    </>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default VehiclesPage;
